import React, { Dispatch, ReactElement, SetStateAction } from 'react';
import { SvgIcon } from '@material-ui/core';
import { CancelRounded } from '@material-ui/icons';
import { useDispatch } from 'react-redux';
import BasicModal from '../../../../modules/modals/BasicModal';
import JoinActionButton from '../../JoinActionButton';
import {
  Description,
  HeadlineContainer,
  IDVerifiedButton,
  StyledContainer,
  Title,
  TryAgainHeadline,
  selfieWallContentStyle,
  tryAgainModalStyle,
} from '../SelfieVerificationStyle';
import { CloseBtn, DashlineSeparator, closeButton } from '../../../common/CommonStyle';
import useMediaBreakpoints from '../../../../hooks/useMediaBreakpoints';
import {
  facelivenessSkipAction,
  setFaceLivenessStatus,
  setShowSelfieVerificationWall,
  updateProfileLivenessStatus,
} from '../../../../actions/profileActions';
import { ActiveIcon } from '../../../../atoms/icons/IDVIcons';

interface MaxAttemptModalProps {
  hash_id: string;
  showMaxAttemptModal: boolean;
  setShowMaxAttemptModal: Dispatch<SetStateAction<boolean>>;
  setShowIdvConfirmModal: Dispatch<SetStateAction<boolean>>;
  facelivenessStatus?: { message?: string; code?: string | number };
  isJoinFlow: boolean;
  handleRedirectJoin: () => void;
}

const MaxAttemptModal: React.FC<MaxAttemptModalProps> = ({
  hash_id,
  showMaxAttemptModal,
  setShowMaxAttemptModal,
  setShowIdvConfirmModal,
  facelivenessStatus,
  isJoinFlow,
  handleRedirectJoin,
}): ReactElement => {
  const { isMobile } = useMediaBreakpoints();
  const dispatch = useDispatch();

  const errorCode = facelivenessStatus?.code;

  const handleClose = () => {
    if (errorCode === 40125006) {
      dispatch(facelivenessSkipAction(hash_id));
    }

    if (!isJoinFlow) {
      dispatch(updateProfileLivenessStatus());
      dispatch(setShowSelfieVerificationWall(false));
    }

    setShowMaxAttemptModal(false);
    dispatch(setFaceLivenessStatus(null));
    handleRedirectJoin();
  };

  return (
    <BasicModal
      isOpen={showMaxAttemptModal}
      hideClose={true}
      customStyle={{
        // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
        content: {
          ...tryAgainModalStyle.content,
          height: errorCode === 40125006 ? 'auto' : 390,
        },
        overlay: {
          ...tryAgainModalStyle.overlay,
        },
      }}
      customContentStyle={selfieWallContentStyle}
      onClose={handleClose}
      shouldCloseOnOverlayClick={false}
      shouldCloseOnEsc={false}
    >
      <CloseBtn
        onClick={handleClose}
        style={{ top: isMobile ? '-85px' : '-25px', right: isMobile ? '-18px' : '-42px' }}
      >
        <SvgIcon component={CancelRounded} style={closeButton} />
      </CloseBtn>
      <HeadlineContainer isJoinFlow={true}>
        <Title>Selfie Verification</Title>
        <DashlineSeparator style={{ width: '100%' }} />
        <TryAgainHeadline isJoinFlow={isJoinFlow}>Max attempts reached</TryAgainHeadline>
        <Description isJoinFlow={isJoinFlow}>
          You have reached the maximum attempts allowed to capture your selfie.
        </Description>
        {errorCode === 40125006 && (
          <Description isJoinFlow={isJoinFlow}>Please allow 24 hours to try again.</Description>
        )}
        {errorCode === 40125005 && (
          <Description customStyle={{ fontWeight: 700 }}>Your profile is currently under review.</Description>
        )}
        <DashlineSeparator style={{ width: '100%' }} />
      </HeadlineContainer>
      <StyledContainer>
        <div style={{ textAlign: 'center' }}>
          <JoinActionButton
            label={'Close'}
            isDefault={true}
            onClick={handleClose}
            customStyle={{ marginBottom: 15, minWidth: '190px', borderRadius: 4, width: 'auto' }}
          />
        </div>
      </StyledContainer>
      {errorCode === 40125006 && (
        <>
          <DashlineSeparator style={{ width: '100%' }} />
          <Description
            isJoinFlow={isJoinFlow}
          >{`Can’t wait 24 hours? Purchase ID verification and show everyone you’re real with an ID Verified badge!`}</Description>
          <IDVerifiedButton
            buttonType={'gradientTeal'}
            onClick={() => {
              setShowIdvConfirmModal(true);
              dispatch(setFaceLivenessStatus(null));
              setShowMaxAttemptModal(false);
            }}
            icon={<ActiveIcon customStyle={{ fontSize: 24, width: 24, height: 24, marginRight: 4, fill: '#fff' }} />}
          >
            Get ID Verified
          </IDVerifiedButton>
        </>
      )}
    </BasicModal>
  );
};

export default MaxAttemptModal;
